import { useState, useEffect, useContext, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/authContext';
import { useEstadoApp } from '../context/EstadoAppContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { initiateSocketConnection } from '../service/socketio.js';

const API = '/api';

// Roles del circuito. 'user' y 'admin' no aparecen: no son destino de pagos ni de
// certificaciones, así que no hay evento que los tenga.
const ROLES = ['seguimiento', 'legal', 'contable', 'admin'];

const TIPOS = [
  { valor: 'pago_realizado', etiqueta: 'Pago realizado' },
  { valor: 'certificacion_cargada', etiqueta: 'Certificación cargada' },
];

const ETIQUETA_ROL = {
  legal: 'Legal',
  contable: 'Contable',
  seguimiento: 'Seguimiento',
  admin: 'Admin',
  user: 'Usuario',
};

const nombreRol = (rol) => ETIQUETA_ROL[rol] || rol || '—';

// Pantalla de admin para ver los avisos del circuito: qué se notificó, de qué rol salió,
// a qué rol fue y quién lo leyó.
//
// La lectura por usuario es lo que hace útil esta pantalla. Con el 'leida' compartido
// por el rol (como estaba antes de la migración 071), el aviso solo podía estar leído o
// no leído para todo el rol, y no había forma de saber a quién le llegó y a quién no.
// Ahora el backend trae, por evento, quiénes lo leyeron (con fecha) y quiénes siguen sin
// leer, contra el snapshot de destinatarios del momento en que se emitió.
export default function SeguimientoEventosPage() {
  const { token, role, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { cambiarEstadoApp } = useEstadoApp();
  const { setItem } = useLocalStorage();

  const [eventos, setEventos] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState({ eventos: 0, conPendientes: 0, conLectores: 0 });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  // Toggle admin: qué eventos envían notificaciones (migración 075).
  const [config, setConfig] = useState([]);
  const [configError, setConfigError] = useState('');
  const [guardandoConfig, setGuardandoConfig] = useState('');

  const [abierto, setAbierto] = useState(null);
  const [arrayEditando, setArrayEditando] = useState([]);

  // Filtros
  const [rolDestino, setRolDestino] = useState('');
  const [rolOrigen, setRolOrigen] = useState('');
  const [tipo, setTipo] = useState('');
  const [obra, setObra] = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [pagina, setPagina] = useState(0);
  const POR_PAGINA = 25;

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Bloqueos de edición (para decidir si el doble click abre en solo lectura).
  useEffect(() => {
    if (!token) return undefined;
    const s = initiateSocketConnection();
    const onArrayEditando = (data) => setArrayEditando(Array.isArray(data) ? data : []);
    s.on('array_editando', onArrayEditando);
    fetch(`${API}/arrayEditando`)
      .then((r) => r.json())
      .then((data) => setArrayEditando(Array.isArray(data) ? data : []))
      .catch(() => setArrayEditando([]));
    return () => { s.off('array_editando', onArrayEditando); };
  }, [token]);

  const cargar = async () => {
    setCargando(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (rolDestino) params.set('rol_destino', rolDestino);
      if (rolOrigen) params.set('rol_origen', rolOrigen);
      if (tipo) params.set('tipo', tipo);
      if (obra.trim()) params.set('obra', obra.trim());
      if (soloPendientes) params.set('solo_pendientes', 'true');
      params.set('limite', POR_PAGINA);
      params.set('offset', pagina * POR_PAGINA);

      const res = await fetch(`${API}/notificaciones/seguimiento?${params.toString()}`, { headers });
      if (!res.ok) throw new Error('Error al cargar el seguimiento de eventos');
      const data = await res.json();

      setEventos(data.eventos || []);
      setTotal(data.total || 0);
      if (data.resumen) setResumen(data.resumen);
    } catch (e) {
      setError(e.message);
    }
    setCargando(false);
  };

  useEffect(() => {
    if (role !== 'admin') return;
    cargar();
  }, [role, rolDestino, rolOrigen, tipo, soloPendientes, pagina]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar la config de eventos una vez (el resto de los efectos es de filtros).
  useEffect(() => {
    if (role !== 'admin') return;
    let activo = true;
    (async () => {
      try {
        const res = await fetch(`${API}/notificaciones/config`, { headers });
        if (!res.ok) throw new Error('Error al cargar la configuración de eventos');
        const data = await res.json();
        if (activo) setConfig(data.data || []);
      } catch (e) {
        if (activo) setConfigError(e.message);
      }
    })();
    return () => { activo = false; };
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enciende/apaga un evento y refleja el cambio al toque.
  const toggleEvento = async (evento, activo) => {
    setGuardandoConfig(evento);
    setConfigError('');
    try {
      const res = await fetch(`${API}/notificaciones/config`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventos: [{ evento, activo }] })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || 'Error al guardar la configuración');
      }
      const data = await res.json();
      setConfig((prev) => prev.map((c) => (c.evento === evento ? (data.data?.[0] || { ...c, activo }) : c)));
    } catch (e) {
      setConfigError(e.message);
    }
    setGuardandoConfig('');
  };

  // Buscar por obra es explícito ( botón), no por cada tecla: si no, cada pulsación
  // dispara un request y la tabla parpadea mientras se escribe el código.
  const buscarObra = () => {
    setPagina(0);
    cargar();
  };

  const limpiarFiltros = () => {
    setRolDestino('');
    setRolOrigen('');
    setTipo('');
    setObra('');
    setSoloPendientes(false);
    setPagina(0);
  };

  // Doble click en el código de obra para abrirla, igual que TablaObras y
  // ChatBurbujaGlobal: hay que dejar los datos en localStorage y cambiar estadoApp antes
  // de navegar, porque InicioPage solo lee el query param en esos estados.
  // Verifica si OTRO usuario (distinto al actual) está editando la obra.
  const isEditingByOtherUser = (obraId) => {
    const safeArray = Array.isArray(arrayEditando) ? arrayEditando : [];
    return safeArray.some((objeto) => {
      if (!objeto || !objeto.id_edit || !objeto.user_edit || !objeto.status) return false;
      const userEditClean = String(objeto.user_edit).replace(/"/g, '');
      return objeto.id_edit === obraId && objeto.status === 'editando' && userEditClean !== user;
    });
  };

  const abrirObra = (codigo) => {
    if (!codigo) return;
    setItem('idPartidoObra', codigo);
    // Si otro usuario está editando, InicioPage entra en solo lectura; si no,
    // en modo edición normal. Antes se guardaba 'true' siempre.
    const otroEdita = isEditingByOtherUser(codigo);
    setItem('isEditingPagos', otroEdita ? 'true' : 'false');
    cambiarEstadoApp('edit_desde_pagos', { obra: codigo, origen: 'seguimiento_eventos' });
    navigate(`/inicio?id_partido_obra=${encodeURIComponent(codigo)}`);
  };

  const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-AR') : '—');

  if (role !== 'admin') {
    return (
      <div className="p-8 text-center text-gray-500">
        Acceso denegado. Solo administradores.
      </div>
    );
  }

  const paginas = Math.ceil(total / POR_PAGINA);

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl font-bold mb-1">Seguimiento de eventos</h1>
      <p className="text-sm text-gray-500 mb-4">
        Avisos del circuito de pagos y certificaciones, con quién los leyó y quién sigue sin leer.
      </p>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-500">Eventos</div>
          <div className="text-2xl font-bold">{resumen.eventos}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 rounded-lg p-3">
          <div className="text-xs text-gray-500">Con alguien sin leer</div>
          <div className="text-2xl font-bold text-amber-600">{resumen.conPendientes}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg p-3">
          <div className="text-xs text-gray-500">Leídos por alguien</div>
          <div className="text-2xl font-bold text-green-600">{resumen.conLectores}</div>
        </div>
      </div>

      {/* Toggle admin: eventos que envían notificaciones */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-semibold">Eventos que envían notificaciones</span>
          <span className="text-xs text-gray-500">el circuito llega experimental: acá se apagan sin tocar código</span>
        </div>
        {configError && <div className="text-xs text-red-600 mb-2">{configError}</div>}
        {config.length === 0 && !configError ? (
          <div className="text-sm text-gray-400">Cargando…</div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {config.map((c) => (
              <label
                key={c.evento}
                className={`flex items-start gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer select-none ${
                  c.activo
                    ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40'
                }`}
              >
                <input
                  type="checkbox"
                  checked={c.activo}
                  disabled={guardandoConfig === c.evento}
                  onChange={(e) => toggleEvento(c.evento, e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium block">{TIPOS.find((t) => t.valor === c.evento)?.etiqueta || c.evento}</span>
                  <span className="text-xs text-gray-500">{c.descripcion}</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rol destino</label>
          <select
            value={rolDestino}
            onChange={(e) => { setRolDestino(e.target.value); setPagina(0); }}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
          >
            <option value="">Todos</option>
            {ROLES.map((r) => <option key={r} value={r}>{nombreRol(r)}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Rol origen</label>
          <select
            value={rolOrigen}
            onChange={(e) => { setRolOrigen(e.target.value); setPagina(0); }}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
          >
            <option value="">Todos</option>
            {ROLES.map((r) => <option key={r} value={r}>{nombreRol(r)}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Tipo</label>
          <select
            value={tipo}
            onChange={(e) => { setTipo(e.target.value); setPagina(0); }}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
          >
            <option value="">Todos</option>
            {TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.etiqueta}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Obra</label>
          <div className="flex gap-1">
            <input
              value={obra}
              onChange={(e) => setObra(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') buscarObra(); }}
              placeholder="025-002"
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 w-28"
            />
            <button
              onClick={buscarObra}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Buscar
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm pb-1">
          <input
            type="checkbox"
            checked={soloPendientes}
            onChange={(e) => { setSoloPendientes(e.target.checked); setPagina(0); }}
          />
          Solo con pendientes
        </label>

        <button
          onClick={limpiarFiltros}
          className="text-sm text-blue-600 hover:text-blue-800 underline pb-1"
        >
          Limpiar filtros
        </button>
      </div>

      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

      {/* Tabla */}
      <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700 text-left">
            <tr>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Origen</th>
              <th className="px-3 py-2">Destino</th>
              <th className="px-3 py-2">Obra</th>
              <th className="px-3 py-2 text-center">Leídos</th>
              <th className="px-3 py-2 text-center">Sin leer</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {cargando && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">Cargando…</td></tr>
            )}
            {!cargando && eventos.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">Sin eventos para estos filtros</td></tr>
            )}
            {!cargando && eventos.map((ev) => (
              // Fragment y no '<>' porque el key tiene que estar en el elemento que
              // devuelve el map: con '<>' React no lo encuentra y avisa por cada fila.
              <Fragment key={ev.id}>
                <tr className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 whitespace-nowrap">{fecha(ev.fecha_creacion)}</td>
                  <td className="px-3 py-2">
                    {TIPOS.find((t) => t.valor === ev.tipo)?.etiqueta || ev.tipo}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="font-medium">{nombreRol(ev.rol_origen)}</span>
                    {ev.usuario_origen && <span className="text-gray-500"> · {ev.usuario_origen}</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{nombreRol(ev.rol_destino)}</td>
                  <td
                    className="px-3 py-2 cursor-pointer hover:underline text-blue-700 dark:text-blue-400 whitespace-nowrap"
                    onDoubleClick={() => abrirObra(ev.id_partido_obra)}
                    title="Doble click para abrir la obra"
                  >
                    {ev.id_partido_obra || '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ev.totales.leidos}
                    <span className="text-gray-400">/{ev.totales.destinatarios}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ev.totales.pendientes > 0
                      ? <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">{ev.totales.pendientes}</span>
                      : <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">todos</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setAbierto(abierto === ev.id ? null : ev.id)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {abierto === ev.id ? 'Ocultar' : 'Detalle'}
                    </button>
                  </td>
                </tr>
                {abierto === ev.id && (
                  <tr className="bg-gray-50 dark:bg-gray-700/40">
                    <td colSpan={8} className="px-3 py-3">
                      <div className="text-sm mb-3">{ev.mensaje}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-semibold text-gray-500 mb-1">
                            Leídos ({ev.leidos.length})
                          </div>
                          {ev.leidos.length === 0 && <div className="text-sm text-gray-400">Nadie lo leyó</div>}
                          {ev.leidos.map((u) => (
                            <div key={u.id} className="text-sm flex items-center gap-2">
                              <span className="text-green-600">✓</span>
                              <span>{u.nombre}</span>
                              <span className="text-xs text-gray-400">{fecha(u.fecha)}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-500 mb-1">
                            Sin leer ({ev.pendientes.length})
                          </div>
                          {ev.pendientes.length === 0 && <div className="text-sm text-gray-400">Todos lo leyeron</div>}
                          {ev.pendientes.map((u) => (
                            <div key={u.id} className="text-sm flex items-center gap-2">
                              <span className="text-amber-600">•</span>
                              <span>{u.nombre}</span>
                            </div>
                          ))}
                          {ev.destinatarios_estimados && (
                            <div className="text-xs text-gray-400 mt-2">
                              Lista estimada: este aviso es anterior al guardado del padrón de
                              destinatarios, así que se listan los usuarios del rol que existen hoy.
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {paginas > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4 text-sm">
          <button
            onClick={() => setPagina((p) => Math.max(0, p - 1))}
            disabled={pagina === 0}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Anterior
          </button>
          <span>Página {pagina + 1} de {paginas}</span>
          <button
            onClick={() => setPagina((p) => Math.min(paginas - 1, p + 1))}
            disabled={pagina >= paginas - 1}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}